import { useEffect, useState } from "react";
import { X, Download, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const PHONE_RE = /^[6-9]\d{9}$/;
const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export default function SyllabusModal({ open, onClose, courseSlug, courseTitle, syllabusUrl }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) {
      setForm({ name: "", phone: "", email: "" });
      setErrors({});
      setLoading(false);
      setDone(false);
    }
  }, [open]);

  if (!open) return null;

  const change = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    if (errors[k]) setErrors({ ...errors, [k]: null });
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name";
    if (!PHONE_RE.test(form.phone.trim())) next.phone = "Enter a valid 10-digit mobile number";
    if (!EMAIL_RE.test(form.email.trim())) next.email = "Enter a valid email address";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const triggerDownload = (url) => {
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noreferrer";
    a.download = `${courseTitle || "syllabus"}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/enroll", {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        city: "",
        learning_mode: "Online Live",
        message: "Syllabus download request",
        course_slug: courseSlug,
      });
      const url = data?.syllabus_url || syllabusUrl;
      triggerDownload(url);
      setDone(true);
      toast.success("Syllabus download starting…");
      setTimeout(onClose, 1800);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = (err) =>
    `w-full px-4 py-3 rounded-xl border ${err ? "border-red-400" : "border-neutral-200"} bg-white text-sm focus:outline-none focus:border-sky-500`;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="syllabus-modal">
      <div className="bg-white rounded-3xl max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-neutral-100 flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-sky-500 font-semibold">Get the syllabus</div>
            <h3 className="font-display font-bold text-xl mt-1">{courseTitle}</h3>
            <p className="text-xs text-neutral-500 mt-1">Fill in your details to instantly download the PDF.</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-neutral-100 rounded-full shrink-0" data-testid="syllabus-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!done ? (
          <form onSubmit={submit} noValidate className="p-6 space-y-3" data-testid="syllabus-form">
            <div>
              <input placeholder="Full name*" value={form.name} onChange={change("name")}
                     className={inputCls(errors.name)} data-testid="syllabus-name" />
              {errors.name && <div className="text-xs text-red-500 mt-1">{errors.name}</div>}
            </div>
            <div>
              <input type="tel" inputMode="numeric" maxLength={10} placeholder="10-digit mobile number*"
                     value={form.phone} onChange={change("phone")}
                     className={inputCls(errors.phone)} data-testid="syllabus-phone" />
              {errors.phone && <div className="text-xs text-red-500 mt-1">{errors.phone}</div>}
            </div>
            <div>
              <input type="email" placeholder="Email address*" value={form.email} onChange={change("email")}
                     className={inputCls(errors.email)} data-testid="syllabus-email" />
              {errors.email && <div className="text-xs text-red-500 mt-1">{errors.email}</div>}
            </div>
            <label className="flex items-start gap-2 text-xs text-neutral-500">
              <input type="checkbox" defaultChecked className="mt-0.5" />
              <span>I agree to be contacted by EQtech Academy about this course.</span>
            </label>
            <button type="submit" disabled={loading} className="btn-primary-eq w-full justify-center" data-testid="syllabus-submit">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Preparing…</> : <><Download className="w-4 h-4" /> Download Syllabus</>}
            </button>
          </form>
        ) : (
          <div className="p-6 text-center" data-testid="syllabus-done">
            <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
              <Download className="w-5 h-5" />
            </div>
            <div className="font-display font-bold text-lg mt-3">Your syllabus is downloading</div>
            <p className="text-sm text-neutral-500 mt-1">Check your Downloads folder in a moment.</p>
          </div>
        )}
      </div>
    </div>
  );
}
